/**
 * Anchoring: turn a clicked element into something we can find again after
 * the app's markup has moved, and admit it when we cannot.
 *
 * Concept sec 7: "an anchor that breaks silently is worse than one that
 * admits it is stale". Every resolution therefore reports which strategy
 * found the element, and the caller can see that it was not the one that
 * captured it.
 */

import type { Anchor } from "./protocol";

const MAX_TEXT = 120;
const MAX_DEPTH = 12;

/** Our own chrome must never be anchored to, recorded, or highlighted. */
export const IGNORE_CLASS = "loupe-ignore";

export function isOurs(node: Node | null): boolean {
  let el: Element | null = node instanceof Element ? node : node?.parentElement ?? null;
  while (el) {
    if (el.classList?.contains(IGNORE_CLASS)) return true;
    el = el.parentElement;
  }
  return false;
}

function ownText(el: Element): string {
  const text = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  return text.slice(0, MAX_TEXT);
}

/**
 * A CSS path that is short enough to read and specific enough to resolve.
 * Stops early at an id or a data-testid, because those are the parts of a
 * document that survive a refactor.
 */
export function cssPath(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;

  while (node && node.nodeType === Node.ELEMENT_NODE && depth < MAX_DEPTH) {
    // Stop at a handle only when it names ONE element (D-016). A repeated
    // id or test id is a path segment that matches several subtrees, and
    // the path built on it resolves to whichever comes first.
    if (node.id && isUniqueIn(node.ownerDocument, `#${CSS.escape(node.id)}`)) {
      parts.unshift(`#${CSS.escape(node.id)}`);
      break;
    }
    const testid = node.getAttribute("data-testid");
    if (testid && isUniqueIn(node.ownerDocument, testidSelector(testid))) {
      parts.unshift(testidSelector(testid));
      break;
    }
    const tag = node.tagName.toLowerCase();
    const parent: Element | null = node.parentElement;
    if (!parent) {
      parts.unshift(tag);
      break;
    }
    const siblings = Array.from(parent.children).filter((c) => c.tagName === node!.tagName);
    parts.unshift(
      siblings.length > 1 ? `${tag}:nth-of-type(${siblings.indexOf(node) + 1})` : tag,
    );
    node = parent;
    depth += 1;
  }

  return parts.join(" > ");
}

/** Document-space box, so a pin survives scrolling without recalculation. */
export function documentRect(el: Element): Anchor["rect"] {
  const r = el.getBoundingClientRect();
  return {
    x: Math.round(r.left + window.scrollX),
    y: Math.round(r.top + window.scrollY),
    w: Math.round(r.width),
    h: Math.round(r.height),
  };
}

/**
 * Where a click landed inside a box, as a fraction of it ([F-001], AC-091).
 *
 * Proportional and not absolute, because the box is the only thing that
 * survives a reflow: at another window size the element moves and resizes,
 * and a fraction still names the same place on it. Clamped to [0,1] so a
 * click on the border cannot put the pin outside the element it belongs to.
 *
 * A box with no area yields the centre. That is not a real position - it is
 * the honest answer for "somewhere in a thing with no extent", and callers
 * that care must ask `isRendered` first.
 */
export function spotIn(
  box: { left: number; top: number; width: number; height: number },
  point: { clientX: number; clientY: number },
): { fx: number; fy: number } {
  const fraction = (value: number, start: number, extent: number): number => {
    if (!(extent > 0)) return 0.5;
    const raw = (value - start) / extent;
    return Math.min(1, Math.max(0, Number(raw.toFixed(4))));
  };
  return {
    fx: fraction(point.clientX, box.left, box.width),
    fy: fraction(point.clientY, box.top, box.height),
  };
}

/**
 * Does this element occupy space on the page right now? ([F-001] V0.4, AC-092)
 *
 * `getClientRects()` is empty exactly when the element generates no box -
 * `display: none`, a detached subtree, an unselected tab panel, a collapsed
 * section. Such an element still answers `querySelector`, so resolution finds
 * it and `getBoundingClientRect()` hands back a box of all zeros; drawing at
 * that box puts a pin at the origin of the viewport, which looks exactly like
 * a correct pin and points at nothing anybody can see.
 *
 * A zero-area box is not a position. This is the test that says so.
 */
export function isRendered(el: Element): boolean {
  return el.getClientRects().length > 0;
}

const testidSelector = (testid: string): string => `[data-testid="${CSS.escape(testid)}"]`;

/** Does this selector match exactly one element? A handle that matches two is not a handle. */
export function isUniqueIn(root: ParentNode | null, selector: string): boolean {
  if (!root) return false;
  try {
    return root.querySelectorAll(selector).length === 1;
  } catch {
    return false;
  }
}

/**
 * D-016 (2026-09-10): a test id is the strongest handle only when it names
 * ONE element. The commonest use of test ids is the same one on every row of
 * a list - `data-testid="prio"` in each row - and `resolveAnchor` takes the
 * FIRST match for a test id and reports it fresh. So an anchor captured on
 * the third row's cell came back on the first row's, and said nothing.
 *
 * A repeated test id is therefore not recorded as the handle at all; the
 * anchor falls through to the id or the CSS path, which `cssPath` now also
 * builds only on unique segments.
 */
export function anchorFor(el: Element, point?: { clientX: number; clientY: number }): Anchor {
  const raw = el.getAttribute("data-testid");
  const testid = raw && isUniqueIn(el.ownerDocument, testidSelector(raw)) ? raw : null;
  const id = el.id && isUniqueIn(el.ownerDocument, `#${CSS.escape(el.id)}`) ? el.id : "";
  const strategy: Anchor["strategy"] = testid ? "testid" : id ? "id" : "selector";
  return {
    strategy,
    testid,
    selector: cssPath(el),
    tag: el.tagName.toLowerCase(),
    text: ownText(el),
    rect: documentRect(el),
    spot: point ? spotIn(el.getBoundingClientRect(), point) : null,
  };
}

export interface Resolution {
  el: Element | null;
  /** True when the element was found by a weaker route than the one that captured it. */
  stale: boolean;
  by: "testid" | "id" | "selector" | "text" | "none";
}

/**
 * Is this resolution worth reporting as *Anker veraltet*? (D-026)
 *
 * "Veraltet" is a statement about an ANCHOR, not about the page in front of
 * us. The count used to be `found.stale || !found.el`, and `!found.el` is the
 * ordinary condition of every entry written on another page - so the badge
 * went to warn with the reviewer's first entry and never came back down.
 *
 * Two things are countable, and they differ in what we actually know:
 *
 *   - FOUND BY A WEAKER ROUTE than the one that captured it. The element is
 *     on this page and the handle we stored no longer names it. True whatever
 *     page we are on, and exactly what the badge claims.
 *   - NOT FOUND AT ALL, on the very route the entry was captured on. There
 *     "not here" cannot mean "another page", so the anchor is broken.
 *
 * Not found on any OTHER route says nothing, and nothing is what it is worth.
 * An entry whose route we never learned is not counted either: a silent miss
 * is better than a number that is wrong in one direction all the time.
 */
export function isStaleAnchor(
  found: Pick<Resolution, "el" | "stale">,
  page: { capturedRoute?: string; here: string },
): boolean {
  if (found.el) return found.stale;
  return page.capturedRoute !== undefined && page.capturedRoute === page.here;
}

/**
 * Find the anchored element again. Tries the strategies strongest-first and
 * reports which one worked, so "found, but not the way we stored it" is a
 * visible state rather than a silent success.
 */
export function resolveAnchor(a: Anchor, root: Document = document): Resolution {
  // D-027: the uniqueness test D-016 introduced belongs at BOTH ends. Capture
  // refuses a repeated test id as a handle; resolution has to refuse one that
  // became repeated after capture - a list that grew a second row carrying the
  // same id. Without it `querySelector` takes the first match and reports the
  // strongest strategy, which is the silent wrong element D-016 was about,
  // reached by the other door.
  if (a.testid && isUniqueIn(root, testidSelector(a.testid))) {
    const el = root.querySelector(testidSelector(a.testid));
    if (el) return { el, stale: false, by: "testid" };
  }

  try {
    const el = root.querySelector(a.selector);
    // Reaching the selector after the anchor was captured by testid means the
    // testid is gone - found, but not the way we stored it.
    if (el) return { el, stale: a.strategy === "testid", by: "selector" };
  } catch {
    /* a selector can stop being valid; fall through to the text match */
  }

  if (a.text) {
    const candidates = Array.from(root.querySelectorAll(a.tag));
    const hit = candidates.find((c) => ownText(c) === a.text);
    if (hit) return { el: hit, stale: true, by: "text" };
  }

  return { el: null, stale: true, by: "none" };
}
